/**
 * abibelle-review-form.js
 * Formulaire d'avis Abibelle — V1
 * Responsabilité : comportement uniquement.
 *
 * Ce fichier ne modifie aucune classe CSS existante.
 * Il ne touche pas aux composants déjà validés :
 *   - étoiles de la fiche produit
 *   - section Avis clients
 *   - moyenne / distribution / cache
 *   - Quick Add / cartes produits
 *
 * Seul style inline autorisé : body { position, top, width }
 * pour le scroll lock iOS Safari — retiré à la fermeture.
 */

(function () {
  'use strict';

  /* ── Constantes ─────────────────────────────────────────────────────────── */

  const SUBMIT_ENDPOINT  = 'https://abibelle-review.vercel.app/api/reviews/submit';
  const UPLOAD_ENDPOINT  = 'https://abibelle-review.vercel.app/api/reviews/upload';
  const MAX_PHOTOS       = 5;
  const MAX_PHOTO_BYTES  = 4 * 1024 * 1024;  // 4 Mo (limite Vercel 4,5 Mo par requête)
  const ALLOWED_TYPES    = ['image/jpeg', 'image/png', 'image/webp'];

  const MAX_DIMENSION    = 2048;   // px — largeur ou hauteur max après redim
  const COMPRESS_QUALITY = 0.82;  // qualité JPEG — bon compromis qualité/poids

  const SEL = {
    overlay:        '#abibelle-review-modal',
    openTrigger:    '[data-abibelle-review-open]',
    closeTrigger:   '[data-abibelle-review-close]',
    form:           '#abibelle-review-form',
    productRef:     '#abibelle-review-product-ref',
    productImage:   '#abibelle-review-product-image',
    productTitle:   '#abibelle-review-product-title',
    starsGroup:     '#abibelle-review-stars',
    starBtns:       '#abibelle-review-stars .abibelle-review-form__star',
    ratingInput:    '#abibelle-review-rating',
    starError:      '#abibelle-review-star-error',
    nameInput:      '#abibelle-review-name',
    nameError:      '#abibelle-review-name-error',
    emailInput:     '#abibelle-review-email',
    emailError:     '#abibelle-review-email-error',
    bodyInput:      '#abibelle-review-body',
    bodyError:      '#abibelle-review-body-error',
    submitBtn:      '#abibelle-review-submit',
    submitLabel:    '#abibelle-review-submit .abibelle-review-form__submit-label',
    submitLoader:   '#abibelle-review-submit .abibelle-review-form__submit-loading',
    errorGlobal:    '#abibelle-review-error-global',
    confirmation:   '#abibelle-review-confirmation',
    formBody:       '#abibelle-review-form',
    panel:          '.abibelle-review-form__panel',
    photosInput:    '#abibelle-review-photos-input',
    photosPreviews: '#abibelle-review-photos-previews',
    photosCounter:  '#abibelle-review-photos-counter',
    photosLabel:    '#abibelle-review-photos-label',
    photosError:    '#abibelle-review-photos-error',
  };

  /* ── État interne ───────────────────────────────────────────────────────── */

  let savedScrollY  = 0;
  let isSubmitting  = false;
  let selectedFiles = [];  // File[]

  /* ── Références DOM ─────────────────────────────────────────────────────── */

  const overlay = document.querySelector(SEL.overlay);
  if (!overlay) return;

  const form           = overlay.querySelector(SEL.form);
  const productRef     = overlay.querySelector(SEL.productRef);
  const productImage   = overlay.querySelector(SEL.productImage);
  const productTitle   = overlay.querySelector(SEL.productTitle);
  const starsGroup     = overlay.querySelector(SEL.starsGroup);
  const ratingInput    = overlay.querySelector(SEL.ratingInput);
  const starError      = overlay.querySelector(SEL.starError);
  const nameInput      = overlay.querySelector(SEL.nameInput);
  const nameError      = overlay.querySelector(SEL.nameError);
  const emailInput     = overlay.querySelector(SEL.emailInput);
  const emailError     = overlay.querySelector(SEL.emailError);
  const bodyInput      = overlay.querySelector(SEL.bodyInput);
  const bodyError      = overlay.querySelector(SEL.bodyError);
  const submitBtn      = overlay.querySelector(SEL.submitBtn);
  const submitLabel    = overlay.querySelector(SEL.submitLabel);
  const submitLoader   = overlay.querySelector(SEL.submitLoader);
  const errorGlobal    = overlay.querySelector(SEL.errorGlobal);
  const confirmation   = overlay.querySelector(SEL.confirmation);
  const rootUrl        = overlay.dataset.rootUrl || '/';
  const photosInput    = overlay.querySelector(SEL.photosInput);
  const photosPreviews = overlay.querySelector(SEL.photosPreviews);
  const photosCounter  = overlay.querySelector(SEL.photosCounter);
  const photosLabel    = overlay.querySelector(SEL.photosLabel);
  const photosError    = overlay.querySelector(SEL.photosError);

  /* ── Scroll lock (iOS Safari safe) ─────────────────────────────────────── */

  function lockScroll() {
    savedScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top      = `-${savedScrollY}px`;
    document.body.style.width    = '100%';
  }

  function unlockScroll() {
    document.body.style.position = '';
    document.body.style.top      = '';
    document.body.style.width    = '';
    window.scrollTo(0, savedScrollY);
  }

  /* ── Ouverture ──────────────────────────────────────────────────────────── */

  function openForm(trigger) {
    const pid   = trigger.dataset.productId    || '';
    const title = trigger.dataset.productTitle || '';
    const image = trigger.dataset.productImage || '';

    if (productRef)   productRef.value        = pid;
    if (productTitle) productTitle.textContent = title;
    if (productImage && image) {
      productImage.src = image;
      productImage.alt = title;
    }

    resetForm();

    overlay.removeAttribute('hidden');
    lockScroll();

    const firstFocusable = overlay.querySelector(
      'button, [href], input:not([type="hidden"]), textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) firstFocusable.focus();
  }

  /* ── Fermeture ──────────────────────────────────────────────────────────── */

  function closeForm() {
    overlay.setAttribute('hidden', '');
    unlockScroll();
  }

  /* ── Réinitialisation ───────────────────────────────────────────────────── */

  function resetForm() {
    setRating(0);

    if (form) form.reset();
    if (productRef) productRef.value = productRef.value;

    clearAllFieldErrors();
    hideError();
    if (starError) starError.hidden = true;

    if (form) {
      form.hidden        = false;
      form.style.display = '';
      form.scrollTop     = 0;
    }
    if (confirmation) confirmation.hidden = true;

    /* Réinitialiser les photos */
    selectedFiles = [];
    updatePhotosUI();
    clearPhotosError();

    setSubmitState('idle');
  }

  /* ── Gestion des erreurs par champ ─────────────────────────────────────── */

  const ERROR_CLASS = 'abibelle-review-form__input--error';

  function setFieldError(input, errorEl, message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden      = false;
    if (input) input.classList.add(ERROR_CLASS);
  }

  function clearFieldError(input, errorEl) {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden      = true;
    if (input) input.classList.remove(ERROR_CLASS);
  }

  function clearAllFieldErrors() {
    clearFieldError(nameInput,  nameError);
    clearFieldError(emailInput, emailError);
    clearFieldError(bodyInput,  bodyError);
    if (starError) starError.hidden = true;
  }

  /* ── Étoiles ────────────────────────────────────────────────────────────── */

  function setRating(value) {
    if (!starsGroup) return;

    const stars = starsGroup.querySelectorAll('.abibelle-review-form__star');
    stars.forEach((star) => {
      const starVal = parseInt(star.dataset.value, 10);
      star.setAttribute('aria-pressed', starVal <= value ? 'true' : 'false');
    });

    if (ratingInput) ratingInput.value = value > 0 ? value : '';
  }

  function initStars() {
    if (!starsGroup) return;

    const stars = starsGroup.querySelectorAll('.abibelle-review-form__star');

    stars.forEach((star) => {
      star.addEventListener('click', () => {
        const val = parseInt(star.dataset.value, 10);
        setRating(val);
        if (starError) starError.hidden = true;
      });

      star.addEventListener('mouseenter', () => {
        previewRating(parseInt(star.dataset.value, 10));
      });

      star.addEventListener('mouseleave', () => {
        setRating(parseInt(ratingInput?.value || '0', 10));
      });

      star.addEventListener('keydown', (e) => {
        const current = parseInt(ratingInput?.value || '0', 10);
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          e.preventDefault();
          setRating(Math.min(5, current + 1));
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          e.preventDefault();
          setRating(Math.max(1, current - 1));
        }
      });
    });
  }

  function previewRating(value) {
    if (!starsGroup) return;
    const stars = starsGroup.querySelectorAll('.abibelle-review-form__star');
    stars.forEach((star) => {
      const starVal = parseInt(star.dataset.value, 10);
      star.setAttribute('aria-pressed', starVal <= value ? 'true' : 'false');
    });
  }

  /* ── États du bouton d'envoi ────────────────────────────────────────────── */

  function setSubmitState(state, message) {
    if (!submitBtn) return;

    if (state === 'loading') {
      submitBtn.disabled = true;
      if (submitLabel)  submitLabel.hidden  = true;
      if (submitLoader) {
        submitLoader.hidden      = false;
        submitLoader.textContent = message || 'Envoi en cours…';
      }
    } else {
      submitBtn.disabled = false;
      if (submitLabel)  submitLabel.hidden  = false;
      if (submitLoader) {
        submitLoader.hidden      = true;
        submitLoader.textContent = '';
      }
    }
  }

  /* ── Erreur globale ─────────────────────────────────────────────────────── */

  function showError(message) {
    if (!errorGlobal) return;
    errorGlobal.textContent = message;
    errorGlobal.hidden      = false;
    errorGlobal.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function hideError() {
    if (!errorGlobal) return;
    errorGlobal.textContent = '';
    errorGlobal.hidden      = true;
  }

  /* ── Gestion des photos ─────────────────────────────────────────────────── */

  function clearPhotosError() {
    if (!photosError) return;
    photosError.textContent = '';
    photosError.hidden      = true;
  }

  function setPhotosError(message) {
    if (!photosError) return;
    photosError.textContent = message;
    photosError.hidden      = false;
  }

  function updatePhotosUI() {
    if (!photosPreviews) return;

    /* Libérer les anciennes object URLs */
    photosPreviews.querySelectorAll('img').forEach(img => {
      if (img.src.startsWith('blob:')) URL.revokeObjectURL(img.src);
    });

    photosPreviews.innerHTML = '';

    selectedFiles.forEach((file, idx) => {
      const thumb = document.createElement('div');
      thumb.className = 'abibelle-review-form__photo-thumb';

      const img = document.createElement('img');
      img.src = URL.createObjectURL(file);
      img.alt = file.name;

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'abibelle-review-form__photo-remove';
      removeBtn.setAttribute('aria-label', `Retirer la photo ${idx + 1}`);
      removeBtn.textContent = '✕';
      removeBtn.addEventListener('click', () => {
        URL.revokeObjectURL(img.src);
        selectedFiles.splice(idx, 1);
        updatePhotosUI();
      });

      thumb.appendChild(img);
      thumb.appendChild(removeBtn);
      photosPreviews.appendChild(thumb);
    });

    /* Compteur */
    if (photosCounter) {
      photosCounter.textContent = `${selectedFiles.length} / ${MAX_PHOTOS}`;
    }

    /* Masquer le bouton ajouter si max atteint */
    if (photosLabel) {
      photosLabel.classList.toggle(
        'abibelle-review-form__photos-add--hidden',
        selectedFiles.length >= MAX_PHOTOS
      );
    }

    /* Mention discrète retirer */
    const existingHint = photosPreviews.parentElement
      ? photosPreviews.parentElement.querySelector('.abibelle-review-form__photos-hint')
      : null;

    if (selectedFiles.length > 0) {
      if (!existingHint) {
        const hintEl = document.createElement('p');
        hintEl.className = 'abibelle-review-form__photos-hint';
        hintEl.textContent = 'Appuie sur × pour retirer une photo.';
        photosPreviews.insertAdjacentElement('afterend', hintEl);
      }
    } else {
      if (existingHint) existingHint.remove();
    }
  }

  /* ── Compression canvas — uniquement si file.size > MAX_PHOTO_BYTES ─────── */

  async function compressImage(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img  = new Image();

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error(`Impossible de lire ${file.name}. Vérifie que le fichier n'est pas corrompu.`));
      };

      img.onload = () => {
        URL.revokeObjectURL(url);

        let { width, height } = img;

        // Redimensionner uniquement si nécessaire
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          if (width >= height) {
            height = Math.round((height / width) * MAX_DIMENSION);
            width  = MAX_DIMENSION;
          } else {
            width  = Math.round((width / height) * MAX_DIMENSION);
            height = MAX_DIMENSION;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width  = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error(`Ton navigateur ne supporte pas le traitement d'image.`));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error(`Impossible de compresser ${file.name}.`));
              return;
            }
            const compressedName = file.name.replace(/\.[^.]+$/, '.jpg');
            resolve(new File([blob], compressedName, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          COMPRESS_QUALITY
        );
      };

      img.src = url;
    });
  }

  /* ── Gestion des photos — sélection et compression conditionnelle ────────── */

  function initPhotos() {
    if (!photosInput) return;

    photosInput.addEventListener('change', async () => {
      clearPhotosError();
      const incoming = Array.from(photosInput.files || []);

      // Validation du format uniquement — la taille est gérée par compression
      for (const file of incoming) {
        if (!ALLOWED_TYPES.includes(file.type)) {
          setPhotosError(`Format non accepté : ${file.name}. Utilise JPG, PNG ou WEBP.`);
          photosInput.value = '';
          return;
        }
      }

      const remaining = MAX_PHOTOS - selectedFiles.length;
      const toAdd     = incoming.slice(0, remaining);

      if (incoming.length > remaining) {
        setPhotosError(
          `Maximum ${MAX_PHOTOS} photos. Seules les ${remaining} premières ont été ajoutées.`
        );
      }

      // Compression conditionnelle — uniquement si file.size > MAX_PHOTO_BYTES
      const processed = [];
      for (const file of toAdd) {
        if (file.size <= MAX_PHOTO_BYTES) {
          // Fichier dans les limites — conservé tel quel
          processed.push(file);
        } else {
          // Fichier trop lourd — compression silencieuse
          let compressed;
          try {
            compressed = await compressImage(file);
          } catch (err) {
            setPhotosError(err.message);
            photosInput.value = '';
            return;
          }

          // Vérification post-compression
          if (compressed.size > MAX_PHOTO_BYTES) {
            setPhotosError(
              `${file.name} est trop lourde même après compression. Utilise une photo de moins de 4 Mo.`
            );
            photosInput.value = '';
            return;
          }

          processed.push(compressed);
        }
      }

      selectedFiles = [...selectedFiles, ...processed];
      photosInput.value = '';
      updatePhotosUI();
    });
  }

  /* ── Upload séquentiel — une photo à la fois ────────────────────────────── */

  async function uploadPhotosSequentially() {
    const total = selectedFiles.length;
    const gids  = [];

    for (let i = 0; i < total; i++) {
      setSubmitState('loading', `Téléversement des photos… (${i + 1}/${total})`);

      const file     = selectedFiles[i];
      const formData = new FormData();
      formData.append('photos', file);

      let data;
      try {
        const res = await fetch(UPLOAD_ENDPOINT, {
          method: 'POST',
          body:   formData,
        });

        data = await res.json();

        if (!res.ok || !data.ok) {
          throw new Error(data?.error || `Erreur lors de l'upload de ${file.name}.`);
        }
      } catch (err) {
        throw new Error(err.message || `Impossible d'envoyer ${file.name}. Vérifie ta connexion.`);
      }

      gids.push(...data.gids);
    }

    return gids;
  }

  /* ── Validation côté client ─────────────────────────────────────────────── */

  function validateForm() {
    clearAllFieldErrors();

    let firstErrorEl = null;

    // 1. Note obligatoire
    const rating = parseInt(ratingInput?.value || '0', 10);
    if (!rating || rating < 1 || rating > 5) {
      if (starError) {
        starError.textContent = 'Choisis une note.';
        starError.hidden = false;
      }
      if (!firstErrorEl) firstErrorEl = starError || starsGroup;
    }

    // 2. Nom obligatoire (uniquement si visible — cliente non connectée)
    if (nameInput) {
      if (!nameInput.value.trim()) {
        setFieldError(nameInput, nameError, 'Entre ton nom ou ton prénom.');
        if (!firstErrorEl) firstErrorEl = nameInput;
      }
    }

    // 3. Courriel — champ vide vs format invalide
    if (emailInput) {
      const emailVal   = emailInput.value.trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailVal) {
        setFieldError(emailInput, emailError, 'Entre ton adresse courriel.');
        if (!firstErrorEl) firstErrorEl = emailInput;
      } else if (!emailRegex.test(emailVal)) {
        setFieldError(emailInput, emailError, 'Entre une adresse courriel valide.');
        if (!firstErrorEl) firstErrorEl = emailInput;
      }
    }

// 4. Commentaire — obligatoire, non vide
    if (bodyInput) {
if (!bodyInput.value.trim()) {
setFieldError(bodyInput, bodyError, 'Ton commentaire est requis.');
        if (!firstErrorEl) firstErrorEl = bodyInput;
      }
    }

    if (firstErrorEl) {
      firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      if (typeof firstErrorEl.focus === 'function') {
        setTimeout(() => firstErrorEl.focus(), 300);
      }
      return false;
    }

    return true;
  }

  /* ── Soumission ─────────────────────────────────────────────────────────── */

  async function handleSubmit(e) {
    e.preventDefault();

    if (isSubmitting) return;

    hideError();

    if (!validateForm()) return;

    isSubmitting = true;

    const formData = new FormData(form);
    const payload  = {};

    for (const [key, value] of formData.entries()) {
      payload[key] = value;
    }

    if (payload.rating) {
      payload.rating = parseInt(payload.rating, 10);
    }

    /* ── Upload séquentiel des photos ───────────────────────────────────── */
    if (selectedFiles.length > 0) {
      let gids;
      try {
        gids = await uploadPhotosSequentially();
      } catch (uploadErr) {
        showError(uploadErr.message);
        setSubmitState('idle');
        isSubmitting = false;
        return;
      }
      payload.photos = gids;
    }

    /* ── Soumission de l'avis ───────────────────────────────────────────── */
    setSubmitState('loading', 'Envoi de votre avis…');

    try {
      const response = await fetch(SUBMIT_ENDPOINT, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });

      let data    = {};
      let rawText = '';
      try {
        rawText = await response.text();
        data    = JSON.parse(rawText);
      } catch {
        console.warn('[ARF] Réponse non-JSON:', rawText);
      }

      if (response.ok && data.ok === true) {
        showConfirmation();
      } else {
        const message = data.error
          || (Array.isArray(data.errors) && data.errors.map(err => err.message).join(' '))
          || 'Une erreur est survenue. Réessaie dans quelques instants.';
        showError(message);
        setSubmitState('idle');
      }

    } catch (networkErr) {
      showError(`Erreur technique : ${networkErr?.message || 'Erreur réseau inconnue'}`);
      setSubmitState('idle');
    } finally {
      isSubmitting = false;
    }
  }

  /* ── Écran de confirmation ──────────────────────────────────────────────── */

  function showConfirmation() {
    if (form) {
      form.scrollTop     = 0;
      form.hidden        = true;
      form.style.display = 'none';
    }

    if (confirmation) confirmation.hidden = false;

    setSubmitState('idle');
  }

  /* ── Fermeture par clic sur l'overlay ───────────────────────────────────── */

  function handleOverlayClick(e) {
    if (e.target === overlay) closeForm();
  }

  /* ── Fermeture par Escape ───────────────────────────────────────────────── */

  function handleKeydown(e) {
    if (e.key === 'Escape' && !overlay.hasAttribute('hidden')) {
      closeForm();
    }
  }

  /* ── Piège de focus (accessibilité) ────────────────────────────────────── */

  function handleFocusTrap(e) {
    if (overlay.hasAttribute('hidden')) return;

    const focusable = Array.from(overlay.querySelectorAll(
      'button:not([disabled]), [href], input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));

    if (!focusable.length) return;

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ── Initialisation ─────────────────────────────────────────────────────── */

  function init() {
    document.querySelectorAll(SEL.openTrigger).forEach((trigger) => {
      trigger.addEventListener('click', () => openForm(trigger));
    });

    overlay.querySelectorAll(SEL.closeTrigger).forEach((btn) => {
      btn.addEventListener('click', closeForm);
    });

    overlay.addEventListener('click', handleOverlayClick);
    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('keydown', handleFocusTrap);

    initStars();
    initPhotos();

    if (form) form.addEventListener('submit', handleSubmit);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
